import { DateTime } from 'luxon';
import { Frequency as RRuleFrequency, RRule } from 'rrule';

import { DAYS } from './constants.js';
import { type MonitoringHoursRule, MonitoringHoursType } from '../../models/index.js';

const WEEKDAY_VALUES = [0, 1, 2, 3, 4];
const WEEKEND_VALUES = [5, 6];

export const ruleLocalTime = (rule: MonitoringHoursRule, timeZone?: string): DateTime | null => {
  if (!rule.startDate || !rule.localStartTime) {
    return null;
  }
  // Read the calendar date in UTC and re-anchor it in the patient's zone. Reading it directly in the
  // patient's zone would shift the rule a day earlier whenever startDate is stored as naive midnight.
  const startDate = DateTime.fromISO(rule.startDate, { zone: 'utc' });
  if (!startDate.isValid) {
    return null;
  }
  const localStart = DateTime.fromISO(`${startDate.toFormat('yyyy-MM-dd')}T${rule.localStartTime}`, {
    zone: timeZone || 'local',
  });
  return localStart.isValid ? localStart : null;
};

export const ruleEndTime = (rule: MonitoringHoursRule, timeZone?: string): DateTime | null => {
  const start = ruleLocalTime(rule, timeZone);
  if (!start || rule.durationMinutes === undefined) {
    return null;
  }
  return start.plus({ minutes: rule.durationMinutes });
};

export const ruleTimeRangeLabel = (rule: MonitoringHoursRule, timeZone?: string): string => {
  const start = ruleLocalTime(rule, timeZone);
  const end = ruleEndTime(rule, timeZone);
  if (!start || !end) {
    return 'Time not set';
  }
  return `${start.toFormat('h:mm a')} – ${end.toFormat('h:mm a')}`;
};

const daysLabel = (byDay: number[]): string => {
  const sorted = [...byDay].sort((first, second) => first - second);
  if (sorted.length === 7) {
    return 'Every day';
  }
  if (sorted.length === WEEKDAY_VALUES.length && WEEKDAY_VALUES.every((day) => sorted.includes(day))) {
    return 'Weekdays';
  }
  if (sorted.length === WEEKEND_VALUES.length && WEEKEND_VALUES.every((day) => sorted.includes(day))) {
    return 'Weekends';
  }
  return sorted.map((day) => DAYS.find((option) => option.value === day)?.label.slice(0, 3) ?? '').join(', ');
};

export const ruleDaysLabel = (rule: MonitoringHoursRule, timeZone?: string): string => {
  if (!rule.repeats) {
    const start = ruleLocalTime(rule, timeZone);
    return start ? start.toFormat('LLL d, yyyy') : 'One-off';
  }
  if (rule.freq === RRuleFrequency.WEEKLY && rule.byDay?.length) {
    return daysLabel(rule.byDay);
  }
  return 'Every day';
};

export const ruleRecurrenceLabel = (rule: MonitoringHoursRule): string => {
  if (!rule.repeats) {
    return "doesn't repeat";
  }
  const interval = rule.interval ?? 1;
  if (rule.freq === RRuleFrequency.WEEKLY) {
    return interval === 1 ? 'repeats weekly' : `repeats every ${interval} weeks`;
  }
  return interval === 1 ? 'repeats daily' : `repeats every ${interval} days`;
};

export const ruleTypeLabel = (rule: MonitoringHoursRule): string => {
  if (rule.type !== MonitoringHoursType.CHECK_IN) {
    return 'Full monitoring';
  }
  if (!rule.checkInDuration || !rule.checkInInterval) {
    return 'Check-in';
  }
  const cadence = rule.checkInInterval === 1 ? 'hour' : `${rule.checkInInterval} hours`;
  return `Check-in · ${rule.checkInDuration} min every ${cadence}`;
};

// A one-off rule carries `until` set to the far-future sentinel the create form sends when no end
// date is given, so it can only be judged by whether its single occurrence has already finished.
export const hasRuleEnded = (rule: MonitoringHoursRule, timeZone?: string): boolean => {
  if (!rule.repeats) {
    const end = ruleEndTime(rule, timeZone);
    return !!end && end < DateTime.now();
  }
  if (!rule.until) {
    return false;
  }
  const until = DateTime.fromISO(rule.until);
  return until.isValid && until < DateTime.now();
};

export const ruleNextOccurrence = (rule: MonitoringHoursRule, timeZone?: string): DateTime | null => {
  const start = ruleLocalTime(rule, timeZone);
  if (!start) {
    return null;
  }
  if (!rule.repeats || rule.freq === undefined || rule.freq === null) {
    return start;
  }
  const recurrence = new RRule({
    freq: rule.freq,
    interval: rule.interval ?? 1,
    byweekday: rule.byDay?.length ? rule.byDay : undefined,
    dtstart: start.toJSDate(),
    until: rule.until ? DateTime.fromISO(rule.until).toJSDate() : null,
  });
  const next = recurrence.after(new Date(), true);
  return next ? DateTime.fromJSDate(next).setZone(timeZone || 'local', { keepLocalTime: true }) : start;
};
